import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';

import { SortingProps } from '@block-explorer/ui';

import {
  ArrowRightIcon,
  FlexRow,
  formatNumber,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';

import { ApiPaginatedGetResult } from '../../hooks/event-store-hooks';
import { usePaginationState } from '../../router/navigation';
import { sortNumericFields } from '../../utils/sorting';
import { Trans, useTranslation } from 'react-i18next';
import { RowsPerPage } from './rows-per-page';
import { PaginationArrowButton, PaginationButton } from './pagination-button';
import { PaginationInput } from './pagination-input';
import { PaginationParams } from '../../services';
import { TableToFeedSwitch } from './table-to-feed-switch';
import type { TableViewId } from '../../redux/table-view';
import { SEARCH_RESULTS_PATH } from '../../router/paths';

const Container = styled(FlexRow)<{
  hasLeftSideContent?: boolean;
}>(({ theme, hasLeftSideContent }) =>
  theme.withMedia({
    minHeight: !hasLeftSideContent ? [60, 48] : [100, 48],
    height: ['auto', 48],
    flexDirection: ['column', 'row', 'row'],
    justifyContent: !hasLeftSideContent ? 'flex-end' : 'space-between',
    padding: ['16px', '12px 20px'],
    margin: ['0 0 10px 0', '0'],
    '& > * + *': {
      marginTop: [20, 0],
    },
  })
);

const StyledFlexRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '100%',
  })
);

const MirroredSvgIcon = styled(SvgIcon)(({ theme }) => ({
  transform: 'rotate(180deg)',
}));

export const useClientPagination = <Entity extends any>({
  paginationStateKey,
  arr,
  requestParams,
}: {
  paginationStateKey: string;
  arr: Entity[] | null;
  requestParams?: Partial<PaginationParams> & { defaultPageSize?: number };
}): Pick<
  ApiPaginatedGetResult<Entity>,
  'data' | 'paginationProps' | 'sortingProps'
> & { resetState?: () => void } => {
  const {
    currentPage: currentPageParam,
    setCurrentPage: setPaginationPage,
    perPage: perPageParam,
    setPerPage: setPaginationPerPage,
    orderBy,
    orderDirection,
    setOrder,
    resetPaginationState,
  } = usePaginationState(paginationStateKey, requestParams?.defaultPageSize);

  const { perPage, currentPage } = {
    currentPage: currentPageParam,
    perPage: perPageParam,
    ...requestParams,
  };

  const total = arr?.length || 0;

  const paginationProps: PaginationProps = {
    itemCount: total,
    pageCount: Math.ceil(total / perPage) || 0,
    currentPage: currentPage,
    setCurrentPage: (page: number) => {
      if (page > 0 && page <= total) {
        setPaginationPage(page);
      }
    },
    perPage: perPage,
    setPerPage: setPaginationPerPage,
  };

  const sortingProps: SortingProps = {
    orderBy: orderBy,
    orderDirection: orderDirection,
    setOrder,
  };

  const data =
    arr &&
    arr
      .sort((fieldA, fieldB) =>
        orderBy === null || orderBy === undefined
          ? 0
          : sortNumericFields(fieldA[orderBy], fieldB[orderBy], orderDirection)
      )
      .filter((_, index) => {
        const minIndex = (currentPage - 1) * perPage;
        const maxIndex = currentPage * perPage;
        return index >= minIndex && index < maxIndex;
      });

  const resetState = () => {
    resetPaginationState();
  };

  return {
    data,
    paginationProps: { ...paginationProps, perPage: paginationProps.perPage },
    sortingProps,
    resetState,
  };
};

export enum TableViewType {
  'table' = 'table',
  'secondary' = 'secondary',
}

export interface PaginationProps {
  perPage: number;
  itemCount?: number;
  pageCount?: number;
  currentPage?: number;
  setPerPage?: (limit: number) => void;
  setCurrentPage?: (page: number) => void;
}

export interface TableSettingsProps extends PaginationProps {
  hideRowsPerPage?: boolean;
  hideTotalCount?: boolean;
  totalRowsLabel?: string;
  tableId?: TableViewId;
  hidePaginationOnError?: boolean;
  filter?: JSX.Element;
  actions?: JSX.Element | JSX.Element[];
  checkbox?: JSX.Element;
}

const ROWS_PER_PAGE = [10, 25, 50, 100];
const SEARCH_RESULTS_ROWS_PER_PAGE = [5, ...ROWS_PER_PAGE];

export function TableSettings({
  perPage = 10,
  itemCount = 0,
  pageCount = 1,
  currentPage = 1,
  setCurrentPage = () => {},
  setPerPage = () => {},
  hideRowsPerPage = false,
  hideTotalCount = false,
  hidePaginationOnError = true,
  totalRowsLabel = 'total rows',
  tableId,
  filter,
  actions,
  checkbox,
}: TableSettingsProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const rowsPerPage =
    pathname === SEARCH_RESULTS_PATH
      ? SEARCH_RESULTS_ROWS_PER_PAGE
      : ROWS_PER_PAGE;
  const prevPageHandler = () => {
    setCurrentPage(currentPage - 1);
  };

  const nextPageHandler = () => {
    setCurrentPage(currentPage + 1);
  };

  const firstPageHandler = () => {
    setCurrentPage(1);
  };

  const lastPageHandler = () => {
    setCurrentPage(pageCount);
  };

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
  };

  const isFirstEnabled = currentPage > 1;
  const isPrevEnabled = currentPage > 1;
  const isNextEnabled = currentPage < pageCount;
  const isLastEnabled = currentPage < pageCount;
  const hasActions = Array.isArray(actions)
    ? actions.length > 0
    : Boolean(actions);
  const hasFilter = Boolean(filter);
  const hasLeftSideContent = !hideTotalCount || Boolean(checkbox);

  const onMobile = (
    <Container hasLeftSideContent={hasLeftSideContent}>
      {!hideTotalCount && hasActions ? (
        <StyledFlexRow>
          <BodyText size={3} variation="darkGray">
            <Trans t={t}>
              {{ rows: formatNumber(itemCount) }} {totalRowsLabel}
            </Trans>
          </BodyText>
        </StyledFlexRow>
      ) : null}
      <StyledFlexRow
        itemsSpacing={12}
        align={'center'}
        justify={!hideTotalCount || hasFilter ? 'space-between' : 'flex-end'}
      >
        {!hideTotalCount && !hasActions ? (
          <BodyText size={3} variation="darkGray">
            <Trans t={t}>
              {{ rows: formatNumber(itemCount) }} {totalRowsLabel}
            </Trans>
          </BodyText>
        ) : null}
        {!!checkbox && checkbox}
        {hasActions && actions}
        {hasFilter && filter}
        {!hideRowsPerPage && (
          <RowsPerPage
            value={perPage}
            items={rowsPerPage}
            onChange={setPerPage}
          />
        )}
        {tableId && <TableToFeedSwitch tableId={tableId} />}
      </StyledFlexRow>
      <StyledFlexRow itemsSpacing={4}>
        <PaginationButton
          color={'secondaryRed'}
          disabled={!isFirstEnabled}
          onClick={firstPageHandler}
        >
          {t('First', 'First')}
        </PaginationButton>
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isPrevEnabled}
          onClick={prevPageHandler}
        >
          <MirroredSvgIcon src={ArrowRightIcon} />
        </PaginationArrowButton>
        <PaginationInput
          pageCount={pageCount}
          currentPage={currentPage}
          onChange={handlePaginationChange}
        />
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isNextEnabled}
          onClick={nextPageHandler}
        >
          <SvgIcon src={ArrowRightIcon} role={'img'} alt={'Arrow icon right'} />
        </PaginationArrowButton>
        <PaginationButton
          color={'secondaryRed'}
          disabled={!isLastEnabled}
          onClick={lastPageHandler}
        >
          {t('Last', 'Last')}
        </PaginationButton>
      </StyledFlexRow>
    </Container>
  );

  const onAbove = (
    <Container hasLeftSideContent={hasLeftSideContent}>
      {!hideTotalCount ? (
        <BodyText size={3} variation="darkGray">
          <Trans t={t}>
            {{ rows: formatNumber(itemCount) }} {totalRowsLabel}
          </Trans>
        </BodyText>
      ) : (
        checkbox || null
      )}
      <FlexRow itemsSpacing={24}>
        {!!actions && actions}
        {!!filter && filter}
        {!hideRowsPerPage && (
          <RowsPerPage
            value={perPage}
            items={rowsPerPage}
            onChange={setPerPage}
          />
        )}
        {tableId && <TableToFeedSwitch tableId={tableId} />}
        <FlexRow itemsSpacing={4}>
          <PaginationButton
            disabled={!isFirstEnabled}
            onClick={firstPageHandler}
            color={'secondaryRed'}
          >
            {t('First', 'First')}
          </PaginationButton>
          <PaginationArrowButton
            color={'secondaryRed'}
            disabled={!isPrevEnabled}
            onClick={prevPageHandler}
          >
            <MirroredSvgIcon src={ArrowRightIcon} alt={'Arrow left icon'} />
          </PaginationArrowButton>
          <PaginationInput
            pageCount={pageCount}
            currentPage={currentPage}
            onChange={handlePaginationChange}
          />
          <PaginationArrowButton
            color={'secondaryRed'}
            disabled={!isNextEnabled}
            onClick={nextPageHandler}
          >
            <SvgIcon
              src={ArrowRightIcon}
              alt={'Arrow right icon'}
              role={'img'}
            />
          </PaginationArrowButton>
          <PaginationButton
            disabled={!isLastEnabled}
            onClick={lastPageHandler}
            color={'secondaryRed'}
          >
            {t('Last', 'Last')}
          </PaginationButton>
        </FlexRow>
      </FlexRow>
    </Container>
  );

  const responsivePagination = useMatchMedia(
    [onMobile, onAbove],
    [perPage, currentPage, pageCount, setCurrentPage]
  );

  const noData = pageCount < 1;
  if (noData && hidePaginationOnError) {
    return (
      <Container hideTotalCount={hideTotalCount}>
        <span>&nbsp;</span>
      </Container>
    );
  }

  return responsivePagination;
}

export default TableSettings;
